import { useMemo } from 'react';

import CropSquareOutlined from '@mui/icons-material/CropSquareOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { Box, Typography } from '@mui/material';

import Button from '@/components/Buttons/Button';
import UpgradeToPremiumButton from '@/components/UpgradeToPremium/UpgradeToPremiumButton';
import { spacing } from '@/constants/ui';
import { FACTSHEETS_ENDPOINT } from '@/features/Factsheet/constants';
import type { FactsheetDetails } from '@/features/Factsheet/types';
import { formatFactsheetDate, getVersionHistoryField } from '@/features/Factsheet/utils';
import useUserDateFormat from '@/hooks/useUserDateFormat';
import useUserDetails from '@/hooks/useUserDetails';
import ShareButton from '@/pages/Markets/MarketsComponents/Share/ShareButton';
import { useGxTokenFromStorage } from '@/services/storage';
import downloadPDF from '@/utils/downloadPDF';

import useItemsTab from '../FactsheetBody/Tabs/ItemsTab/useItemsTab';

interface ButtonSectionProps {
  factsheet: FactsheetDetails;
}

const ButtonSection = ({ factsheet }: ButtonSectionProps) => {
  const { isUserBasic, isUserExpired } = useUserDetails();
  const { handleAllItemsSelection, allItemsSelected } = useItemsTab(factsheet);

  const handleDownload = (): void => {
    downloadPDF(`${FACTSHEETS_ENDPOINT}/${factsheet.code}.pdf`, `${factsheet.code}.pdf`);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
      {(isUserBasic || isUserExpired) && <UpgradeToPremiumButton type='upgrade' />}
      <ShareButton
        code={factsheet.code}
        iconSize={12}
        sx={{ width: '120px' }}
        title={factsheet.title}
      />
      <Button
        color='secondary'
        disabled={allItemsSelected}
        size='medium'
        startIcon={<CropSquareOutlined />}
        variant='contained'
        onClick={handleAllItemsSelection}
      >
        Add item(s) to selector
      </Button>
      <Button
        color='secondary'
        size='medium'
        startIcon={<FileDownloadOutlined />}
        variant='contained'
        onClick={handleDownload}
      >
        Download factsheet
      </Button>
    </Box>
  );
};

interface DescriptionProps {
  description: string | undefined;
}

const Description = ({ description }: DescriptionProps) => {
  if (!description) return null;

  const lines = description.split('\n');

  return (
    <Box>
      {lines.map((line, index) => (
        <Typography
          key={`desc-${index}`}
          sx={theme => ({
            mb: 0.5,
            fontSize: theme.typography.pxToRem(20)
          })}
        >
          {line}
        </Typography>
      ))}
    </Box>
  );
};

interface VersionInfoProps {
  version: string;
  validFrom: string;
  validTo: string;
}

const VersionInfo = ({ version, validFrom, validTo }: VersionInfoProps) => (
  <Typography
    sx={{ pt: 1 }}
    variant='body1'
  >
    Factsheet version {version}
    {validFrom && `, valid from ${validFrom}`}
    {validTo && ` to ${validTo}`}
  </Typography>
);

interface FactsheetHeaderProps {
  factsheet: FactsheetDetails;
}

const FactsheetHeader = ({ factsheet }: FactsheetHeaderProps) => {
  const gxToken = useGxTokenFromStorage();
  const dateFormat = useUserDateFormat();

  const validFrom = useMemo(
    () =>
      formatFactsheetDate(
        getVersionHistoryField(factsheet, 'factsheetValidFrom'),
        dateFormat.value
      ),
    [dateFormat.value, factsheet]
  );

  const validTo = useMemo(
    () =>
      formatFactsheetDate(getVersionHistoryField(factsheet, 'factsheetValidTo'), dateFormat.value),
    [dateFormat.value, factsheet]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
        px: 3,
        width: '100%'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1, mr: spacing['6'], maxWidth: 740, minWidth: 0 }}>
          <Typography variant='h3'>{factsheet.title}</Typography>
        </Box>
        <Box
          display='flex'
          sx={{ py: spacing['2'], flexShrink: 0 }}
        >
          {gxToken && <ButtonSection factsheet={factsheet} />}
        </Box>
      </Box>
      <Description description={factsheet.description} />
      <VersionInfo
        validFrom={validFrom}
        validTo={validTo}
        version={factsheet.latestFactsheetVersion}
      />
    </Box>
  );
};

export default FactsheetHeader;
